import {
  SingleNamedObject,
  NamedObjectContainer,
  createSvgAndSvgObjects,
  HtmlGraphicObject,
  FixedDimension,
  PercentDimension,
  SvgRectangleArea,
  getHtmlOffset
} from "../../web-graphics";
import { MeasurerLine } from "./measurer-line";

export class Cursor extends HtmlGraphicObject {
  constructor(settings) {
    super(settings);
    HtmlGraphicObject.setHtmlObject(
      this.settings,
      createSvgAndSvgObjects(
        "svg",
        ["x", `${this.settings.htmlObject.locationInPixels.x}px`],
        ["y", `${this.settings.htmlObject.locationInPixels.y}px`],
        ["min-width", `${this.settings.htmlObject.sizeInPixels.width}px`],
        ["width", `${this.settings.htmlObject.sizeInPixels.width}px`],
        ["height", `${this.settings.htmlObject.sizeInPixels.height}px`],
        ["style", `margin: 0px; position: absolute;`]
      )
    );
    this.backgroundRectangle = createSvgAndSvgObjects(
      "rect",
      ["x", `0px`],
      ["y", `0px`],
      ["width", `${this.settings.sizeInPixels.width}`],
      ["height", `${this.settings.sizeInPixels.height}`],
      ["fill", "transparent"],
      ["stroke", "transparent"],
      ["style", `margin: 0px; position: absolute;`]
    );
    this.settings.htmlObject.ref.appendChild(this.backgroundRectangle);
    this.settings.isVisible = true;
    this.htmlParentOffset = getHtmlOffset(this.settings.htmlObject.parent.ref);

    let lineTouchAreaX = this.settings.startLinePos - this.settings.touchLineAreaWidth / 2;
    let lineTouchAreaWidth = this.settings.touchLineAreaWidth;
    if (lineTouchAreaX < 0) {
      lineTouchAreaWidth = this.settings.touchLineAreaWidth + lineTouchAreaX;
      lineTouchAreaX = 0;
    }
    if (lineTouchAreaX >= this.settings.sizeInPixels.width) {
      lineTouchAreaWidth = this.settings.touchLineAreaWidth - lineTouchAreaX + this.settings.sizeInPixels.width;
      lineTouchAreaX = this.settings.sizeInPixels.width - 1;
    }
    if (lineTouchAreaX > this.settings.maxX) {
      lineTouchAreaWidth = this.settings.touchLineAreaWidth - lineTouchAreaX + this.settings.maxX + 1;
      lineTouchAreaX = this.settings.maxX;
    }
    this.lineTouchArea = new SvgRectangleArea({
      location: {
        x: new FixedDimension(lineTouchAreaX),
        y: new FixedDimension(0)
      },
      size: {
        width: new FixedDimension(lineTouchAreaWidth),
        height: new PercentDimension(1)
      },
      parent: {
        ref: this
      },
      htmlObject: {
        parent: {
          ref: this.settings.htmlObject.ref
        },
        graphicParent: this
      }
    });
    this.lineTouchArea.settings.htmlObject.ref.setAttribute("cursor", "e-resize");
    //this.lineTouchArea.setBackgroundFillColor("#aaaa00");

    this.linePos = this.settings.startLinePos;
    this.line = new MeasurerLine({
      location: {
        x: new FixedDimension(this.settings.startLinePos),
        y: new FixedDimension(0)
      },
      size: {
        width: new FixedDimension(1),
        height: new PercentDimension(1)
      },
      parent: {
        ref: this
      },
      htmlObject: {
        parent: {
          ref: this.settings.htmlObject.ref
        },
        graphicParent: this
      }
    });
    this.line.setBackgroundFillColor(this.settings.color);

    this.childItemsContainer = new NamedObjectContainer("Items");
    this.childItemsContainer.add(new SingleNamedObject("LineTouchArea", this.lineTouchArea));
    this.childItemsContainer.add(new SingleNamedObject("Line", this.line));

    this.isLineDragging = false;
    this.lineTouchArea.addEventListener(
      "mousedown",
      function () {
        this.isLineDragging = true;
      }.bind(this)
    );

    this.addEventListener("mousemove", this.onMouseMove.bind(this));

    this.addEventListener("mouseup", event => {
      if (!this.isLineDragging) {
        return false;
      }
      this._handleEndMoveEvent(event);
      this.isLineDragging = false;
      return false;
    });

    this.addEventListener("click", event => {
      event.preventDefault();
      this._handleEndMoveEvent(event);
    });
  }

  updateHtmlParentOffset() {
    this.htmlParentOffset = getHtmlOffset(this.settings.htmlObject.parent.ref);
  }

  onMouseMove(event) {
    event.preventDefault();
    if (!this.isLineDragging) {
      return false;
    }
    let x = event.pageX - this.htmlParentOffset.left;
    if (x <= this.settings.maxX) {
      this.line.setLocation(new FixedDimension(x), new FixedDimension(0));
      this.childItemsContainer.forEach(item => {
        item.createBorders();
      });
    }
    return false;
  }

  setDebugMode(debugSettings) {
    super.setDebugMode(debugSettings);
    if (debugSettings.fillHtmlElements) {
      this.setBackgroundFillColor(this.settings.debug.boxFillColor);
    }
    this.childItemsContainer.forEach(item => item.setDebugMode(debugSettings));
  }

  addOnClickedFunction(action) {
    this.settings.htmlObject.ref.addEventListener("click", action);
  }

  addItem(name, htmlGraphicChildObject) {
    this.childItemsContainer.add(new SingleNamedObject(name, htmlGraphicChildObject));
  }

  setBackgroundFillColor(color) {
    this.backgroundRectangle.setAttribute("fill", color);
  }

  setBackgroundStrokeColor(color) {
    this.backgroundRectangle.setAttribute("stroke", color);
  }

  createBorders() {
    super.createBorders();
    this.childItemsContainer.forEach(item => {
      item.setParentSize(this.settings.sizeInPixels.width, this.settings.sizeInPixels.height);
      item.createBorders();
    });
    this.onPositionChanged?.();
  }

  updateChildItemsBorders() {
    this.childItemsContainer.forEach(item => {
      item.createBorders();
    });
    this.onPositionChanged?.();
  }

  createHtmlBorders() {
    super.createHtmlBorders();
    this.backgroundRectangle.setAttribute("width", `${this.settings.sizeInPixels.width}px`);
    this.backgroundRectangle.setAttribute("height", `${this.settings.sizeInPixels.height}px`);
    this.settings.htmlObject.ref.setAttribute("x", `${this.settings.htmlObject.absoluteLocation.x}px`);
    this.settings.htmlObject.ref.setAttribute("y", `${this.settings.htmlObject.absoluteLocation.y}px`);
    this.settings.htmlObject.ref.setAttribute("width", `${this.settings.htmlObject.sizeInPixels.width}px`);
    this.settings.htmlObject.ref.setAttribute("height", `${this.settings.htmlObject.sizeInPixels.height}px`);
  }

  move(x) {
    this._moveLine(x);
    this.updateChildItemsBorders();
  }

  _moveLine(x) {
    if (this.linePos == x) {
      return;
    }
    let lineX = x;
    if (x > this.settings.maxX) {
      lineX = this.settings.maxX;
    }
    let lineTouchAreaX = lineX - this.settings.touchLineAreaWidth / 2;
    let lineTouchAreaWidth = this.settings.touchLineAreaWidth;
    if (lineTouchAreaX < 0) {
      lineTouchAreaX = 0;
      lineTouchAreaWidth = this.settings.touchLineAreaWidth / 2;
    }
    if (lineTouchAreaX + this.settings.touchLineAreaWidth > this.settings.sizeInPixels.width) {
      lineTouchAreaWidth = this.settings.sizeInPixels.width + this.settings.touchLineAreaWidth / 2 - lineX;
    }
    this.linePos = lineX;

    this.line.setLocation(new FixedDimension(lineX), new FixedDimension(0));
    this.lineTouchArea.setLocation(new FixedDimension(lineTouchAreaX), new FixedDimension(0));
    if (lineTouchAreaWidth !== this.lineTouchArea.settings.sizeInPixels.width) {
      this.lineTouchArea.setSize(new FixedDimension(lineTouchAreaWidth), new PercentDimension(1));
    }
  }

  _handleEndMoveEvent(event) {
    const x = event.pageX - this.htmlParentOffset.left;
    this._moveLine(x);
    this.updateChildItemsBorders();
  }
}
